import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import NotificationDropdown from '../components/NotificationDropdown';
import {
  Home,
  FileText,
  User,
  Menu,
  X,
  LogOut,
  Building,
} from 'lucide-react';

const OfficerLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();

 
  const isMinistryAdmin =
    user?.role === 'ministry_admin' ||
    (user?.roles?.some((role) => role.name === 'ministry_admin') ?? false);

  const navigation = [
    {
      name: t('nav.dashboard'),
      href: '/ministry/dashboard',
      icon: Home,
    },
    {
      name: t('nav.grievances'),
      href: '/ministry/grievances',
      icon: FileText,
    },
    // {
    //   name: 'Office Management',
    //   href: '/ministry/offices',
    //   icon: Building,
    // },
    {
      name: 'Reviews',
      href: '/ministry/reviews',
      icon: FileText,
    },
    {
      name: t('nav.profile'),
      href: '/ministry/profile',
      icon: User,
    },
  ];

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ne' : 'en');
  };

  const handleNavigation = () => {
    // Close mobile sidebar after clicking a navigation item
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">

      {/* =========================
          SIDEBAR
      ========================== */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50
          w-64 bg-white shadow-lg
          transform transition-transform duration-300 ease-in-out
          ${
            sidebarOpen
              ? 'translate-x-0'
              : '-translate-x-full'
          }
          md:translate-x-0
        `}
      >

        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b">

          <div className="flex items-center space-x-2">

            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>

            <span className="text-lg font-semibold text-gray-900">
              Ministry Admin
            </span>

          </div>

          {/* Mobile Close Button */}
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-500 hover:text-gray-700"
            aria-label="Close sidebar"
          >
            <X className="h-6 w-6" />
          </button>

        </div>

        {/* Navigation */}
        <nav className="mt-8 px-4">
          <div className="space-y-2">

            {navigation.map((item) => {

              const isActive =
                location.pathname === item.href ||
                location.pathname.startsWith(
                  `${item.href}/`
                );

              const Icon = item.icon;

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={handleNavigation}
                  className={`
                    flex items-center space-x-3
                    px-3 py-2 rounded-md
                    text-sm font-medium
                    transition-colors
                    ${
                      isActive
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}

          </div>
        </nav>

        {/* User Information */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-gray-50">

          <div className="flex items-center space-x-3">

            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-white" />
            </div>

            <div className="flex-1 min-w-0">
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || "User"}
                </p>
              </div>
              <p className="text-xs text-gray-500 truncate">
                {user?.role?.replace("_", " ")}
              </p>
            </div>

          </div>

        </div>

      </aside>

      {/* =========================
          MOBILE OVERLAY
      ========================== */}
      {sidebarOpen && (
        <div
          className="
            fixed inset-0 z-40
            bg-gray-600/75
            md:hidden
          "
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* =========================
          MAIN CONTENT
      ========================== */}
      <div className="md:pl-64">

        {/* =========================
            TOP BAR
        ========================== */}
        <header className="sticky top-0 z-30 bg-white shadow-sm border-b">

          <div className="flex items-center justify-between h-16 px-4">

            {/* Mobile Menu */}
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="
                md:hidden
                text-gray-500
                hover:text-gray-700
              "
              aria-label="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Right Side */}
            <div className="flex items-center space-x-2 ml-auto">

              {/* Notifications */}
              {isMinistryAdmin && (
                <NotificationDropdown />
              )}

              {/* Language Toggle */}
              <button
                type="button"
                onClick={toggleLanguage}
                className="
                  text-gray-700
                  hover:text-primary-600
                  px-3 py-2
                  rounded-md
                  text-sm font-medium
                  transition-colors
                "
              >
                {language === 'en'
                  ? 'नेपाली'
                  : 'English'}
              </button>

              {/* Logout */}
              <button
                type="button"
                onClick={logout}
                className="
                  flex items-center space-x-2
                  text-gray-700
                  hover:text-red-600
                  px-3 py-2
                  rounded-md
                  text-sm font-medium
                  transition-colors
                "
              >
                <LogOut className="h-4 w-4" />

                <span>
                  {t('nav.logout')}
                </span>
              </button>

            </div>

          </div>

        </header>

        {/* =========================
            PAGE CONTENT
        ========================== */}
        <main className="p-6">
          <Outlet />
        </main>

      </div>

    </div>
  );
};

export default OfficerLayout;